import React, { useCallback, useMemo, useState } from 'react';
import { Box, MenuItem, Select, Stack, Typography, } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { BaseDialog } from 'src/components/BaseDialog/BaseDialog';
import { calcImageURL } from 'src/utils/url';
import { ts } from 'src/utils/translate';
import {
    DeleteScope,
    executeDelete
} from 'src/pages/PlannerPage/Planner/dialogs/BulkDeleteDeviceDialog/bulkDeleteDeviceActions';
import { getSourceDeviceInfo, SourceDeviceInfo } from 'src/pages/PlannerPage/utils/device-utils';

interface BulkDeleteDeviceDialogProps {
    onClose: () => void;
    deviceId: number;
}

export function BulkDeleteDeviceDialog({ onClose, deviceId }: BulkDeleteDeviceDialogProps) {
    const [scope, setScope] = useState<DeleteScope>(DeleteScope.SINGLE);
    const [isProcessing, setIsProcessing] = useState(false);
    const sourceInfo: SourceDeviceInfo = useMemo(() => getSourceDeviceInfo(deviceId), [deviceId]);

    const handleConfirm = useCallback(() => {
        setIsProcessing(true);
        executeDelete(deviceId, scope, (success) => {
            setIsProcessing(false);
            if (success) {
                onClose();
            }
        });
    }, [deviceId, scope, onClose]);

    return (
        <BaseDialog
            onClose={onClose}
            disableClose={isProcessing}
            maxWidth="sm"
            title={ts('bulk_delete_dialog_title', 'Delete Device')}
            titleIcon={<DeleteOutlineIcon />}
            cancelLabel={ts('bulk_delete_cancel', 'Cancel')}
            actionLabel={ts('bulk_delete_confirm', 'Delete')}
            onAction={handleConfirm}
            actionDisabled={isProcessing}
        >
            <Stack spacing={3} alignItems="center" sx={{ pt: 2 }}>
                <Stack alignItems="center">
                    <Stack
                        alignItems="center"
                        justifyContent="center"
                        sx={{
                            width: 80,
                            height: 80,
                            borderRadius: '50%',
                            backgroundColor: 'grey.100',
                        }}
                    >
                        <Box
                            component="img"
                            src={calcImageURL(sourceInfo.image)}
                            alt={sourceInfo.productName}
                            sx={{ width: 40, height: 40, objectFit: 'contain' }}
                        />
                    </Stack>
                    <Typography variant="subtitle2">
                        {sourceInfo.productName}
                    </Typography>
                </Stack>

                <Stack spacing={0.5} alignItems="center">
                    <Typography variant="h6" fontWeight="bold">
                        {ts('bulk_delete_confirm_title', 'Confirm Removal')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {ts('bulk_delete_confirm_subtitle', 'Are you sure you want to delete this device?')}
                    </Typography>
                </Stack>

                <Select
                    value={scope}
                    onChange={(e) => setScope(e.target.value as DeleteScope)}
                    fullWidth
                    size="small"
                    sx={{ '& .MuiSelect-select': { whiteSpace: 'normal' } }}
                >
                    <MenuItem value={DeleteScope.SINGLE}>
                        {ts('bulk_delete_scope_single', 'This device')}
                    </MenuItem>
                    <MenuItem value={DeleteScope.ROOM}>
                        {`${ts('bulk_delete_scope_room', 'All same devices in room')} "${sourceInfo.roomName}"`}
                    </MenuItem>
                    <MenuItem value={DeleteScope.FLOOR}>
                        {`${ts('bulk_delete_scope_floor', 'All same devices on floor')} "${sourceInfo.floorName}"`}
                    </MenuItem>
                    <MenuItem value={DeleteScope.FLOOR_ALL}>
                        {ts('bulk_delete_scope_floor_all', 'All devices on floor')}
                    </MenuItem>
                </Select>
            </Stack>
        </BaseDialog>
    );
}